// Single source of truth for the /bob page's Hungarian and English copy.
// Grounded in the real Business Operations Platform product (see
// /Users/reila/development/bob/Business Operations Platform — README.md,
// apps/api/prisma/schema.prisma, apps/web/src/i18n/locales/{hu,en}.json):
// no feature, status, or terminology below is invented.

export type BobLang = "hu" | "en";

type Person = { name: string; role: string; department: string; type: "employee" | "contractor" };
type Department = { name: string; roles: string[] };
type DaySchedule = { day: string; working: boolean; hours?: string };
type SchedulePerson = { name: string; today: string; days: DaySchedule[] };
type ShiftCell = { person: string } | null;
type ShiftRow = { role: string; cells: ShiftCell[] };
type Partner = { name: string; status: string; contact: string; email: string; city: string };
type ActivityItem = { type: string; text: string; date: string };
type DashboardTile = { label: string; value: string };
type TaskRow = { title: string; partner: string; owner: string; due: string; priority: string; status: string };
type DocumentRow = { file: string; size: string; uploadedBy: string; uploadedAt: string; status: string };

export type BobScene = {
  id: string;
  label: string;
  title: string;
  description: string;
  panelTitle: string;
} & (
  | {
      id: "dashboard";
      tiles: DashboardTile[];
      deadlinesHeading: string;
      deadlines: string[];
      activityHeading: string;
      recentActivity: string[];
    }
  | {
      id: "partners";
      columns: { company: string; city: string; email: string; status: string };
      partners: Partner[];
      activityHeading: string;
      activities: ActivityItem[];
    }
  | {
      id: "tasks";
      toggleLabels: [string, string];
      filterLabels: [string, string];
      columns: { title: string; partner: string; owner: string; due: string; priority: string; status: string };
      rows: TaskRow[];
    }
  | { id: "schedule"; people: SchedulePerson[] }
  | { id: "shifts"; days: string[]; rows: ShiftRow[]; warning: string }
  | { id: "employees"; tabs: [string, string]; typeLabels: [string, string]; rows: Person[] }
  | { id: "roles"; departments: Department[] }
  | {
      id: "documents";
      tabs: [string, string];
      uploadLabel: string;
      columns: { file: string; size: string; uploadedBy: string; uploadedAt: string; status: string };
      rows: DocumentRow[];
    }
);

export type BobContent = {
  meta: { title: string; description: string; ogTitle: string; ogDescription: string };
  nav: { wordmark: string; homeLabel: string; backLabel: string; cta: string; langToggleLabel: string };
  hero: {
    heading: string;
    highlight: string;
    headingEnd: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  demo: {
    eyebrow: string;
    heading: string;
    subtitle: string;
    tagline: string;
    workspaceLabel: string;
    workspaceName: string;
    userName: string;
    userRole: string;
    langLabel: string;
    nav: string[];
    scenes: BobScene[];
  };
  audience: {
    eyebrow: string;
    heading: string;
    intro: string;
    items: { title: string; description: string }[];
  };
  value: {
    eyebrow: string;
    heading: string;
    items: { title: string; description: string }[];
  };
  trust: { eyebrow: string; heading: string; items: { title: string; description: string }[] };
  faq: { eyebrow: string; heading: string; items: { question: string; answer: string }[] };
  contact: { heading: string; subtitle: string; emailLabel: string; linkedinLabel: string };
};

const hu: BobContent = {
  meta: {
    title: "BOB: Munkatárs-, alvállalkozó- és partnernyilvántartó szoftver KKV-knak",
    description:
      "A BOB egy helyen mutatja, ki dolgozik a cégnél, mi a felelőssége, mikor dolgozik, és kikkel áll üzleti kapcsolatban a vállalkozás. Munkatárs- és alvállalkozó-nyilvántartás, munkakörök, beosztás, jelenlét és partnerkezelés kis- és középvállalkozásoknak.",
    ogTitle: "BOB: Business Operations Base",
    ogDescription:
      "Munkatársak, alvállalkozók, munkakörök, beosztás, jelenlét és üzleti partnerek egy helyen: a Miller Productions üzemeltetési szoftvere KKV-knak.",
  },
  nav: {
    wordmark: "BOB",
    homeLabel: "Miller Productions",
    backLabel: "Vissza",
    cta: "Kapcsolatfelvétel",
    langToggleLabel: "Nyelv",
  },
  hero: {
    heading: "Kezeld egy helyen a ",
    highlight: "munkatársaidat, partnereidet ",
    headingEnd: "és a cég napi működését.",
    subtitle:
      "A BOB rendben tartja a munkatársak és alvállalkozók nyilvántartását, a munkaköröket és munkarendeket, a beosztást és jelenlétet, valamint az üzleti partnerek adatait és feladatait, hogy ne kelljen táblázatok és üzenetek között keresgélni ahhoz, hogy tudd, ki miért felel, ki dolgozik ma, és mi történt legutóbb egy partnerrel.",
    ctaPrimary: "Kapcsolatfelvétel",
    ctaSecondary: "Így működik",
  },
  demo: {
    eyebrow: "Így működik",
    heading: "Végigkövetve, ahogy a BOB egyben tartja a céget",
    subtitle:
      "Nyolc nézet, a bal oldali menü valós sorrendjében, pontosan úgy, ahogy egy munkatárs végignézné az alkalmazást.",
    tagline: "Üzleti működés alapja",
    workspaceLabel: "Munkaterület",
    workspaceName: "Acme Demo Corp",
    userName: "Mia Manager",
    userRole: "Vezető",
    langLabel: "Magyar",
    nav: ["Áttekintés", "Partnerek", "Feladatok", "Jelenlét", "Munkatársak", "Dokumentumok"],
    scenes: [
      {
        id: "dashboard",
        label: "Áttekintés",
        title: "Minden együtt, egy áttekintésben",
        description:
          "Bejelentkezés után ez az első kép: hány aktív partnered van, hány munkatársad, hány nyitott és lejárt feladat vár, milyen határidők közelednek, és mi történt legutóbb a partnereiddel.",
        panelTitle: "Áttekintés",
        tiles: [
          { label: "Aktív partnerek", value: "18" },
          { label: "Munkatársak", value: "12" },
          { label: "Nyitott feladatok", value: "7" },
          { label: "Lejárt feladatok", value: "1" },
        ],
        deadlinesHeading: "Közelgő határidők",
        deadlines: ["Árajánlat véglegesítése: szept. 10.", "Szerződés-hosszabbítás: szept. 5."],
        activityHeading: "Legutóbbi partneresemények",
        recentActivity: [
          "Zöldkert Kft.: ajánlat elküldve",
          "Napfény Ingatlan Zrt.: egyeztetés lezajlott",
        ],
      },
      {
        id: "partners",
        label: "Partnerek",
        title: "Partnerek és kapcsolattartók",
        description:
          "A cégek, akikkel dolgozol (beszállítók, ügyfelek, érdeklődők), egy helyen, a hozzájuk tartozó kapcsolattartókkal és a legutóbbi eseményekkel együtt.",
        panelTitle: "Partnerek",
        columns: { company: "Cég", city: "Város / Ország", email: "E-mail", status: "Állapot" },
        partners: [
          {
            name: "Zöldkert Kft.",
            status: "Aktív",
            contact: "Fekete Judit",
            email: "info@zoldkert.example.com",
            city: "Szeged, Magyarország",
          },
          {
            name: "Napfény Ingatlan Zrt.",
            status: "Aktív",
            contact: "Balogh Péter",
            email: "kapcsolat@napfenyingatlan.example.com",
            city: "Debrecen, Magyarország",
          },
          {
            name: "Kelet Logisztika Kft.",
            status: "Érdeklődő",
            contact: "Molnár Réka",
            email: "info@keletlogisztika.example.com",
            city: "Miskolc, Magyarország",
          },
        ],
        activityHeading: "Legutóbbi események: Zöldkert Kft.",
        activities: [
          { type: "Hívás", text: "Egyeztetés a következő szállítási ütemezésről", date: "aug. 26." },
          { type: "E-mail", text: "Ajánlat elküldve az őszi rendelésre", date: "aug. 22." },
        ],
      },
      {
        id: "tasks",
        label: "Feladatok",
        title: "Feladatok, partnerhez rendelve",
        description:
          "Minden feladathoz tartozik egy felelős, egy határidő, és ha egy partnerhez kapcsolódik, az adott partner is, úgyhogy nem csak azt látod, mi a teendő, hanem azt is, kinél és miért.",
        panelTitle: "Feladatok",
        toggleLabels: ["Saját feladatok", "Minden feladat"],
        filterLabels: ["Minden állapot", "Minden prioritás"],
        columns: { title: "Cím", partner: "Partner", owner: "Felelős", due: "Határidő", priority: "Prioritás", status: "Állapot" },
        rows: [
          { title: "Árajánlat frissítése a Zöldkertnek", partner: "Zöldkert Kft.", owner: "Mia Manager", due: "aug. 18.", priority: "Magas", status: "Folyamatban" },
          { title: "Szerződés-hosszabbítás előkészítése", partner: "Zöldkert Kft.", owner: "Mia Manager", due: "aug. 27.", priority: "Magas", status: "Folyamatban" },
          { title: "Ajánlat véglegesítése", partner: "Napfény Ingatlan Zrt.", owner: "Eddie Employee", due: "aug. 28.", priority: "Közepes", status: "Nyitott" },
        ],
      },
      {
        id: "schedule",
        label: "Munkarendem",
        title: "Munkarend és jelenlét",
        description:
          "Minden munkatársnak van egy heti munkarendje: mely napokon dolgozik és milyen időben. A napi jelenlét ebből és a jóváhagyott szabadságokból áll össze, nem egy külön, elszakadható nyilvántartásból, úgyhogy nem tud elcsúszni a kettő egymástól.",
        panelTitle: "Munkarend & jelenlét",
        people: [
          {
            name: "Kovács Anna",
            today: "Munkanap · 08:00–16:00",
            days: [
              { day: "H", working: true, hours: "08–16" },
              { day: "K", working: true, hours: "08–16" },
              { day: "Sze", working: true, hours: "08–16" },
              { day: "Cs", working: true, hours: "08–16" },
              { day: "P", working: true, hours: "08–14" },
              { day: "Szo", working: false },
              { day: "V", working: false },
            ],
          },
          {
            name: "Tóth Eszter",
            today: "Jóváhagyott szabadság",
            days: [
              { day: "H", working: true, hours: "09–17" },
              { day: "K", working: true, hours: "09–17" },
              { day: "Sze", working: false },
              { day: "Cs", working: true, hours: "09–17" },
              { day: "P", working: true, hours: "09–17" },
              { day: "Szo", working: false },
              { day: "V", working: false },
            ],
          },
        ],
      },
      {
        id: "shifts",
        label: "Beosztás",
        title: "Műszakbeosztás",
        description:
          "Napi vagy heti nézetben, munkakör szerint csoportosítva látod a beosztást. A rendszer külön jelzi, ha egy műszak fedetlen maradt, és nem enged két egymást átfedő műszakot ugyanarra a munkatársra beosztani.",
        panelTitle: "Műszakbeosztás",
        days: ["H", "K", "Sze", "Cs", "P"],
        rows: [
          { role: "Sales Assistant", cells: [{ person: "Nagy Béla" }, { person: "Nagy Béla" }, null, { person: "Nagy Béla" }, { person: "Nagy Béla" }] },
          { role: "Ügyfélszolgálat", cells: [{ person: "Tóth Eszter" }, { person: "Tóth Eszter" }, { person: "Tóth Eszter" }, null, { person: "Tóth Eszter" }] },
        ],
        warning: "Fedetlen műszak: Szerda, Sales Assistant",
      },
      {
        id: "employees",
        label: "Munkatársak",
        title: "Munkatársak és alvállalkozók",
        description:
          "Egy listában látod, ki alkalmazott és ki alvállalkozó, melyik osztályhoz és munkakörhöz tartozik, és ki kinek a felettese: a szervezeti ábra ebből a valós jelentési struktúrából épül fel, nem egy külön karbantartott dokumentumból.",
        panelTitle: "Munkatársak",
        tabs: ["Munkatársak", "Alvállalkozók"],
        typeLabels: ["Munkatárs", "Alvállalkozó"],
        rows: [
          { name: "Kovács Anna", role: "Sales Manager", department: "Értékesítés", type: "employee" },
          { name: "Nagy Béla", role: "Sales Assistant", department: "Értékesítés", type: "employee" },
          { name: "Tóth Eszter", role: "Ügyfélszolgálati munkatárs", department: "Ügyfélszolgálat", type: "employee" },
          { name: "Szabó Márk", role: "Grafikus", department: "Marketing", type: "contractor" },
          { name: "Varga Kata", role: "Könyvelő", department: "Pénzügy", type: "contractor" },
        ],
      },
      {
        id: "roles",
        label: "Munkakörök",
        title: "Osztályok és munkakörök",
        description:
          "Előbb az osztályokat alakítod ki (pl. Értékesítés, Marketing), utána a hozzájuk tartozó munkaköröket, így minden munkatárs felvételekor és beosztásakor a rendszer csak az adott osztályhoz tartozó munkaköröket ajánlja fel.",
        panelTitle: "Osztályok & munkakörök",
        departments: [
          { name: "Értékesítés", roles: ["Sales Assistant", "Sales Manager"] },
          { name: "Ügyfélszolgálat", roles: ["Ügyfélszolgálati munkatárs", "Csoportvezető"] },
          { name: "Marketing", roles: ["Grafikus", "Tartalomkészítő"] },
        ],
      },
      {
        id: "documents",
        label: "Dokumentumok",
        title: "Céges dokumentumtár",
        description:
          "Szerződések és egyéb fájlok egy helyen, a munkatárs- és partneradatokkal együtt: nem a fő funkció, de ugyanabban a rendszerben van, ahol minden más.",
        panelTitle: "Dokumentumok",
        tabs: ["Aktív", "Archivált"],
        uploadLabel: "Dokumentum feltöltése",
        columns: { file: "Fájl", size: "Méret", uploadedBy: "Feltöltötte", uploadedAt: "Feltöltve", status: "Állapot" },
        rows: [
          { file: "northwind-master-agreement.pdf", size: "125.0 KB", uploadedBy: "Mia Manager", uploadedAt: "2026. aug. 25.", status: "Aktív" },
        ],
      },
    ],
  },
  audience: {
    eyebrow: "Kinek készült",
    heading: "Kisebb csapatoknak, ahol már megéri rendszerezni az alkalmazottakat, feladatokat, partnereket, jelenlétet.",
    intro:
      "A BOB olyan kis- és középvállalkozásoknak való, ahol nincs külön HR-rendszer és külön CRM, de igenis szükség van rá, hogy mindenki átlássa, ki dolgozik a cégnél és kikkel dolgozik együtt a cég.",
    items: [
      {
        title: "Alkalmazottat és alvállalkozót is foglalkoztató csapatok",
        description:
          "Ha a csapatban van külsős és belsős munkavállaló, a BOB mindkettőt ugyanabban a szervezeti struktúrában kezeli, munkakörrel, felettessel és - alvállalkozóknál is releváns esetben - beosztással.",
      },
      {
        title: "Beosztás szerint dolgozó vállalkozások",
        description:
          "Ügyfélszolgálat, kiskereskedelem, szolgáltatóipar, ahol a munkaidő nem 9-től-5-ig egyforma mindenkinek, hanem munkarend és műszakbeosztás szerint alakul. Állítsd be a munkaidőt, amit mindenki nyomon tud követni.",
      },
      {
        title: "Sok külső partnerrel dolgozó cégek",
        description:
          "Beszállítókkal, ügyfelekkel, alvállalkozó partnerekkel egyszerre tartod a kapcsolatot, és fontos, hogy egy helyen lásd, mikor kivel mi történt, és mi a következő lépés.",
      },
    ],
  },
  value: {
    eyebrow: "Milyen problémát old meg",
    heading: "Amit ma táblázatokban és üzenetekben tart nyilván",
    items: [
      {
        title: "Ki miért felel most éppen?",
        description:
          "A munkakörök és osztályok egy helyen vannak rögzítve, nem egy elrejtett file-ban vagy egy régi Excel-táblában. A BOB-ban minden munkatárshoz és alvállalkozóhoz tartozik egy világos munkakör, elérhetőség és felettes.",
      },
      {
        title: "Új munkakör, feladat, kolléga?",
        description:
          "Létrehozhatsz új részlegeket, munkaköröket, és már hozzá is adhatod az új kollégákat. A szervezeti ábra mindenkinek megmutatja a szervezet működését.",
      },
      {
        title: "Ki dolgozik ma, és ki van szabadságon?",
        description:
          "A jelenlét a munkarendből és a jóváhagyott szabadságkérelmekből adódik automatikusan. Nem kell külön file-ban vagy füzetben vezetni, nincs másnak felülírási joga, csak a feletteseknek.",
      },
      {
        title: "Mikor beszéltünk utoljára ezzel a partnerrel, és mi volt a megállapodás?",
        description:
        "Minden partnerhez tartozik egy esemény-előzmény (hívás, e-mail, egyeztetés) és a hozzá kapcsolódó feladatok. Nem kell visszakeresni egy e-mail-fiókban vagy egy kollégánál, vezesd egy helyen a feladatokat.",
      },
      {
        title: "Fontos dokumentumok, ami mindenki számára jó, ha elérhető?",
        description:
        "A BOB egy céges dokumentumtárat is tartalmaz a szerződések és egyéb fájlok tárolására: nem ez a fő funkció, de ugyanabban a rendszerben van, ahol a munkatársak és partnerek adatai.",
      },
    ],
  },
  trust: {
    eyebrow: "Miért bízhatsz benne",
    heading: "Nem prototípus: valós, futó rendszer alapjai",
    items: [
      {
        title: "Szervezetenként elkülönített adatok",
        description:
          "Minden cég adata szervezetszinten van elkülönítve, és ez a szerveroldalon minden egyes kérésnél újra ellenőrzött: nem csak a felületen elrejtett.",
      },
      {
        title: "Szerepkör szerinti jogosultságok",
        description:
          "Adminisztrátor, vezető és munkatárs szerepkörök, finoman szabályozott jogosultságokkal, például ki hagyhat jóvá szabadságot, ki szerkeszthet munkarendet.",
      },
      {
        title: "Naplózott, visszakövethető változtatások",
        description:
          "A biztonság szempontjából érzékeny műveletek (bejelentkezés, szerepkör-változtatás, partner- és feladatmódosítás) naplózva vannak, visszakereshetők.",
      },
    ],
  },
  faq: {
    eyebrow: "GYIK",
    heading: "Kérdések, amik felmerülhetnek",
    items: [
      {
        question: "Mi a különbség a BOB és egy Excel-táblázat vagy egy külön HR- és CRM-rendszer között?",
        answer:
          "Egy táblázat nem jelez, ha két műszak átfedi egymást, és nem köti össze a jelenlétet a munkarenddel. Egy külön HR- és egy külön CRM-rendszer pedig két helyen tartja a céges adatokat, amiket manuálisan kell szinkronban tartani. A BOB egy rendszerben kezeli a munkatársakat, munkaköröket, beosztást és a partnereket, így a kapcsolatok (ki miért felel, ki dolgozik most, melyik feladat melyik partnerhez tartozik) automatikusan összefüggenek.",
      },
      {
        question: "Alvállalkozókat is nyilván lehet tartani, nem csak alkalmazottakat?",
        answer:
          "Igen, a munkatárs-nyilvántartás kifejezetten megkülönbözteti az alkalmazottakat és az alvállalkozókat, mindkettő ugyanabba a szervezeti struktúrába (osztály, munkakör, felettes) tartozik.",
      },
      {
        question: "Hogyan működik a jelenlét-nyilvántartás, kell hozzá külön be- és kijelentkezés?",
        answer:
          "Nem, a jelenlét minden napra a munkatárs munkarendjéből (mely napokon és mikor dolgozik) és a jóváhagyott szabadságkérelmekből adódik. Ez azt jelenti, hogy nincs külön, kézzel vezetett napi jelenléti napló, ami elszakadhatna a tényleges beosztástól.",
      },
      {
        question: "Mennyibe kerül a BOB?",
        answer:
          "A BOB még nem indult el árazott szolgáltatásként. Két fiókmodellt támogat: egy szervezetet kezelő fiókot, és több szervezetet átfogó, üzleti fiókot. Ha érdekel, hogy a vállalkozásodhoz hogyan illeszkedne, vedd fel velünk a kapcsolatot.",
      },
      {
        question: "Milyen vállalkozásoknak ajánlott elsősorban?",
        answer:
          "Olyan kis- és középvállalkozásoknak, ahol kevert a csapat (alkalmazottak és alvállalkozók), a munkaidő beosztás szerint alakul, és rendszeresen dolgoznak külső üzleti partnerekkel (beszállítókkal, ügyfelekkel), akiknél fontos nyomon követni, mi történt és mi a következő lépés.",
      },
    ],
  },
  contact: {
    heading: "Kíváncsi, hogyan illeszkedne a vállalkozásodhoz?",
    subtitle:
      "Vedd fel velünk a kapcsolatot, és megnézzük együtt, hogyan tudná a BOB összefogni a munkatársak, munkarendek és partnerek kezelését a cégednél.",
    emailLabel: "info@millerproductions.hu",
    linkedinLabel: "LinkedIn",
  },
};

const en: BobContent = {
  meta: {
    title: "BOB: Employee, Contractor and Business Partner Management for SMEs",
    description:
      "BOB gives you one place to see who works at your company, what they're responsible for, when they work, and which business partners you deal with. Employee and contractor records, roles, scheduling, attendance, and partner management for small and medium-sized businesses.",
    ogTitle: "BOB: Business Operations Base",
    ogDescription:
      "Employees, contractors, roles, scheduling, attendance and business partners in one place: Miller Productions' operations platform for SMEs.",
  },
  nav: {
    wordmark: "BOB",
    homeLabel: "Miller Productions",
    backLabel: "Back",
    cta: "Get in touch",
    langToggleLabel: "Language",
  },
  hero: {
    heading: "Manage your ",
    highlight: "employees, partners, ",
    headingEnd: "and your company's daily operations, in one place.",
    subtitle:
      "BOB keeps your employee and contractor records, roles and work schedules, shift scheduling and attendance, and your business partners' data and tasks in order, so you're not piecing it together across spreadsheets and message threads to know who's responsible for what, who's working today, and what happened with a partner last.",
    ctaPrimary: "Get in touch",
    ctaSecondary: "See how it works",
  },
  demo: {
    eyebrow: "How it works",
    heading: "Watch how BOB holds the company together",
    subtitle:
      "Eight views, in the real order of the left-hand menu, the same order an employee would actually click through the app.",
    tagline: "Business operations base",
    workspaceLabel: "Workspace",
    workspaceName: "Acme Demo Corp",
    userName: "Mia Manager",
    userRole: "Manager",
    langLabel: "English",
    nav: ["Dashboard", "Partners", "Tasks", "Attendance", "Employees", "Documents"],
    scenes: [
      {
        id: "dashboard",
        label: "Dashboard",
        title: "Everything, in one overview",
        description:
          "This is the first thing you see after signing in: how many active partners you have, how many employees, how many open and overdue tasks, what's due soon, and what happened with your partners most recently.",
        panelTitle: "Dashboard",
        tiles: [
          { label: "Active partners", value: "18" },
          { label: "Employees", value: "12" },
          { label: "Open tasks", value: "7" },
          { label: "Overdue tasks", value: "1" },
        ],
        deadlinesHeading: "Upcoming deadlines",
        deadlines: ["Finalize quote: Sep 10", "Contract renewal: Sep 5"],
        activityHeading: "Recent partner activity",
        recentActivity: [
          "Zöldkert Ltd.: offer sent",
          "Napfény Real Estate Plc.: call completed",
        ],
      },
      {
        id: "partners",
        label: "Partners",
        title: "Partners and contacts",
        description:
          "The companies you work with (suppliers, customers, prospects), in one place, along with their contacts and recent activity.",
        panelTitle: "Partners",
        columns: { company: "Company", city: "City / Country", email: "Email", status: "Status" },
        partners: [
          {
            name: "Zöldkert Ltd.",
            status: "Active",
            contact: "Judit Fekete",
            email: "info@zoldkert.example.com",
            city: "Szeged, Hungary",
          },
          {
            name: "Napfény Real Estate Plc.",
            status: "Active",
            contact: "Péter Balogh",
            email: "contact@napfenyrealestate.example.com",
            city: "Debrecen, Hungary",
          },
          {
            name: "Kelet Logistics Ltd.",
            status: "Prospect",
            contact: "Réka Molnár",
            email: "info@keastlogistics.example.com",
            city: "Miskolc, Hungary",
          },
        ],
        activityHeading: "Recent activity: Zöldkert Ltd.",
        activities: [
          { type: "Call", text: "Discussed the next delivery schedule", date: "Aug 26" },
          { type: "Email", text: "Sent the offer for the autumn order", date: "Aug 22" },
        ],
      },
      {
        id: "tasks",
        label: "Tasks",
        title: "Tasks, tied to a partner",
        description:
          "Every task has an owner, a due date, and, if it relates to one, the partner it belongs to, so you see not just what needs doing, but for whom and why.",
        panelTitle: "Tasks",
        toggleLabels: ["My tasks", "All tasks"],
        filterLabels: ["All statuses", "All priorities"],
        columns: { title: "Title", partner: "Partner", owner: "Owner", due: "Due", priority: "Priority", status: "Status" },
        rows: [
          { title: "Send updated pricing to Zöldkert", partner: "Zöldkert Ltd.", owner: "Mia Manager", due: "Aug 18", priority: "High", status: "In progress" },
          { title: "Prepare contract renewal", partner: "Zöldkert Ltd.", owner: "Mia Manager", due: "Aug 27", priority: "High", status: "In progress" },
          { title: "Finalize quote", partner: "Napfény Real Estate Plc.", owner: "Eddie Employee", due: "Aug 28", priority: "Medium", status: "Open" },
        ],
      },
      {
        id: "schedule",
        label: "My schedule",
        title: "Work schedules and attendance",
        description:
          "Every employee has a weekly schedule: which days they work and what hours. Attendance for any day is worked out from that schedule and any approved leave, not a separate log that can drift out of sync.",
        panelTitle: "Schedules & attendance",
        people: [
          {
            name: "Anna Kovács",
            today: "Working day · 08:00–16:00",
            days: [
              { day: "Mon", working: true, hours: "8–4" },
              { day: "Tue", working: true, hours: "8–4" },
              { day: "Wed", working: true, hours: "8–4" },
              { day: "Thu", working: true, hours: "8–4" },
              { day: "Fri", working: true, hours: "8–2" },
              { day: "Sat", working: false },
              { day: "Sun", working: false },
            ],
          },
          {
            name: "Eszter Tóth",
            today: "Approved leave",
            days: [
              { day: "Mon", working: true, hours: "9–5" },
              { day: "Tue", working: true, hours: "9–5" },
              { day: "Wed", working: false },
              { day: "Thu", working: true, hours: "9–5" },
              { day: "Fri", working: true, hours: "9–5" },
              { day: "Sat", working: false },
              { day: "Sun", working: false },
            ],
          },
        ],
      },
      {
        id: "shifts",
        label: "Shifts",
        title: "Shift schedule",
        description:
          "See the schedule day-by-day or week-by-week, grouped by role. Uncovered shifts are flagged automatically, and the same person can never be assigned to two overlapping shifts.",
        panelTitle: "Shift schedule",
        days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
        rows: [
          { role: "Sales Assistant", cells: [{ person: "Béla Nagy" }, { person: "Béla Nagy" }, null, { person: "Béla Nagy" }, { person: "Béla Nagy" }] },
          { role: "Customer Support", cells: [{ person: "Eszter Tóth" }, { person: "Eszter Tóth" }, { person: "Eszter Tóth" }, null, { person: "Eszter Tóth" }] },
        ],
        warning: "Uncovered shift: Wednesday, Sales Assistant",
      },
      {
        id: "employees",
        label: "Employees",
        title: "Employees and contractors",
        description:
          "One list shows who's an employee and who's a contractor, which department and role they're in, and who they report to: the org chart is built from this real reporting structure, not a separate document someone has to keep updated.",
        panelTitle: "Employees",
        tabs: ["Employees", "Contractors"],
        typeLabels: ["Employee", "Contractor"],
        rows: [
          { name: "Anna Kovács", role: "Sales Manager", department: "Sales", type: "employee" },
          { name: "Béla Nagy", role: "Sales Assistant", department: "Sales", type: "employee" },
          { name: "Eszter Tóth", role: "Support Specialist", department: "Customer Support", type: "employee" },
          { name: "Márk Szabó", role: "Graphic Designer", department: "Marketing", type: "contractor" },
          { name: "Kata Varga", role: "Bookkeeper", department: "Finance", type: "contractor" },
        ],
      },
      {
        id: "roles",
        label: "Roles",
        title: "Departments and work roles",
        description:
          "Departments come first (Sales, Marketing, ...), work roles second, scoped to a department, so when you add or schedule someone, the system only offers roles that actually belong there.",
        panelTitle: "Departments & roles",
        departments: [
          { name: "Sales", roles: ["Sales Assistant", "Sales Manager"] },
          { name: "Customer Support", roles: ["Support Specialist", "Team Lead"] },
          { name: "Marketing", roles: ["Graphic Designer", "Content Writer"] },
        ],
      },
      {
        id: "documents",
        label: "Documents",
        title: "Company document repository",
        description:
          "Contracts and other files in one place, alongside your employee and partner data: not the core function, but part of the same system as everything else.",
        panelTitle: "Documents",
        tabs: ["Active", "Archived"],
        uploadLabel: "Upload document",
        columns: { file: "File", size: "Size", uploadedBy: "Uploaded by", uploadedAt: "Uploaded", status: "Status" },
        rows: [
          { file: "northwind-master-agreement.pdf", size: "125.0 KB", uploadedBy: "Mia Manager", uploadedAt: "Aug 25, 2026", status: "Active" },
        ],
      },
    ],
  },
  audience: {
    eyebrow: "Who it's for",
    heading: "Smaller teams where it's finally worth organizing employees, tasks, partners, and attendance",
    intro:
      "BOB is built for small and medium-sized businesses that don't run separate HR and CRM systems, but still need everyone to have a clear view of who works at the company and who the company works with.",
    items: [
      {
        title: "Teams with both employees and contractors",
        description:
          "If your team has both in-house and outside people, BOB manages both inside the same organizational structure, with a role, a manager, and, where relevant for contractors too, a schedule for each.",
      },
      {
        title: "Businesses that run on schedules and shifts",
        description:
          "Customer support, retail, hospitality: anywhere working hours aren't the same 9-to-5 for everyone, but follow a schedule and shift plan instead. Set up hours everyone can actually keep track of.",
      },
      {
        title: "Companies juggling a lot of outside partners",
        description:
          "Suppliers, customers, contracted partners, all at once, where it matters to see, in one place, who you last talked to, what was agreed, and what's next.",
      },
    ],
  },
  value: {
    eyebrow: "What problem it solves",
    heading: "The things you're currently tracking in spreadsheets and message threads",
    items: [
      {
        title: 'Who\'s actually responsible for this, right now?',
        description:
          "Roles and departments live in one place, not in a hidden file or an old spreadsheet: every employee and contractor in BOB has a clear role, contact details, and manager.",
      },
      {
        title: "A new role, task, or colleague?",
        description:
          "Set up new departments and roles, then add the new colleague straight away. The org chart shows everyone how the organization is actually structured.",
      },
      {
        title: 'Who\'s working today, and who\'s on leave?',
        description:
          "Attendance is worked out automatically from the schedule and approved leave requests: no separate file or notebook to keep updated, and no one but a manager can override it.",
      },
      {
        title: 'When did we last talk to this partner, and what did we agree?',
        description:
          "Every partner has an activity history (calls, emails, meetings) and its own related tasks: no digging through an inbox or asking a colleague, just one place to run the work from.",
      },
      {
        title: "Documents everyone should be able to find?",
        description:
          "BOB also includes a company document repository for contracts and other files: not its core function, but it lives in the same system as your employee and partner data.",
      },
    ],
  },
  trust: {
    eyebrow: "Why trust it",
    heading: "Not a prototype: the foundation of a real, running system",
    items: [
      {
        title: "Data isolated per organization",
        description:
          "Every company's data is isolated at the organization level, re-checked server-side on every single request: not just hidden in the interface.",
      },
      {
        title: "Role-based permissions",
        description:
          "Admin, manager, and employee roles, with fine-grained permissions, for example, who can approve leave, who can edit a work schedule.",
      },
      {
        title: "Logged, auditable changes",
        description:
          "Security-sensitive actions (logins, role changes, partner and task edits) are logged and can be traced back.",
      },
    ],
  },
  faq: {
    eyebrow: "FAQ",
    heading: "Questions you might have",
    items: [
      {
        question: "What's the difference between BOB and a spreadsheet, or a separate HR and CRM system?",
        answer:
          "A spreadsheet won't flag two overlapping shifts, and it won't connect attendance to a schedule. A separate HR system and a separate CRM keep your company data in two places you have to sync by hand. BOB manages employees, roles, scheduling, and partners in one system, so the connections (who's responsible for what, who's working right now, which task belongs to which partner) stay linked automatically.",
      },
      {
        question: "Can I track contractors as well as employees, not just staff?",
        answer:
          "Yes, employee records explicitly distinguish employees from contractors, and both sit inside the same organizational structure (department, role, manager).",
      },
      {
        question: "How does attendance tracking work? Do people have to clock in and out?",
        answer:
          "No, attendance for any given day is derived from an employee's work schedule (which days and hours they're expected to work) and any approved leave requests. There's no separate, manually kept daily attendance log that could drift from the actual schedule.",
      },
      {
        question: "How much does BOB cost?",
        answer:
          "BOB hasn't launched as a priced service yet. It supports two account types: a single-organization account and a business account that spans multiple organizations. Get in touch if you'd like to talk through how it would fit your business.",
      },
      {
        question: "What kind of business is BOB best suited for?",
        answer:
          "Small and medium-sized businesses with a mixed team (employees and contractors), where working hours follow a schedule rather than a fixed 9-to-5, and that deal regularly with outside business partners (suppliers, customers) where it matters to track what happened and what comes next.",
      },
    ],
  },
  contact: {
    heading: "Curious how it would fit your business?",
    subtitle:
      "Get in touch and we'll walk through how BOB could bring your employee, schedule, and partner management together in one place.",
    emailLabel: "info@millerproductions.hu",
    linkedinLabel: "LinkedIn",
  },
};

export const bobContent: Record<BobLang, BobContent> = { hu, en };
